import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LoginPage from "@/pages/LoginPage";
import Dashboard from "@/pages/Dashboard";
import PipelinePage from "@/pages/PipelinePage";
import FollowUpsPage from "@/pages/FollowUpsPage";
import SettingsPage from "@/pages/SettingsPage";
import AdminPage from "@/pages/AdminPage";
import UploadLeadsPage from "@/pages/UploadLeadsPage";
import LeadsPage from "@/pages/LeadsPage";
import HelpPage from "@/pages/HelpPage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import WorkflowsPage from "@/pages/WorkflowsPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import FormsPage from "@/pages/FormsPage";
import FunnelsPage from "@/pages/FunnelsPage";
import InboxPage from "@/pages/InboxPage";
import PipelineConfigPage from "@/pages/PipelineConfigPage";
import CustomFieldsPage from "@/pages/CustomFieldsPage";
import ManualQueuePage from "@/pages/ManualQueuePage";
import Layout from "@/components/Layout";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { useBusinessType } from "@/hooks/useBusinessType";

// Training centre pages
import TrainingDashboard from "@/pages/training/TrainingDashboard";
import RegistrationsPage from "@/pages/training/RegistrationsPage";
import CoursesPage from "@/pages/training/CoursesPage";
import ReviewsPage from "@/pages/training/ReviewsPage";
import SupportPage from "@/pages/training/SupportPage";
import TrainingManualQueuePage from "@/pages/training/ManualQueuePage";
import StudentsPage from "@/pages/training/StudentsPage";
import StudentPipelinePage from "@/pages/training/StudentPipelinePage";
import ReferralsPage from "@/pages/training/ReferralsPage";
import SsoCallbackPage from "@/pages/SsoCallbackPage";
import ReferralLandingPage from "@/pages/ReferralLandingPage";

// Renders the right dashboard based on business type
const SmartDashboard = () => {
  const { isTrainingCentre } = useBusinessType();
  return isTrainingCentre ? <TrainingDashboard /> : <Dashboard />;
};

// Renders the right manual queue based on business type
const SmartManualQueue = () => {
  const { isTrainingCentre } = useBusinessType();
  return isTrainingCentre ? <TrainingManualQueuePage /> : <ManualQueuePage />;
};

// Renders the right students/leads page based on business type
const SmartStudents = () => {
  const { isTrainingCentre } = useBusinessType();
  return isTrainingCentre ? <StudentsPage /> : <LeadsPage />;
};

// Redirects training centres away from general-only pages
const GeneralOnly = ({ children }) => {
  const { isTrainingCentre } = useBusinessType();
  if (isTrainingCentre) return <Navigate to="/dashboard" replace />;
  return children;
};

const ProtectedRoute = ({ children }) => {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const verify = async () => {
      const isValid = await checkAuth();
      if (!isValid) {
        navigate('/login', { replace: true });
      }
      setIsChecking(false);
    };

    verify();
  }, [checkAuth, navigate]);

  if (loading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/sso-callback" element={<SsoCallbackPage />} />
      <Route path="/refer/:code" element={<ReferralLandingPage />} />
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <SmartDashboard />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/pipeline" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <PipelinePage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      <Route path="/leads" element={
        <ProtectedRoute>
          <Layout>
            <SmartStudents />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/followups" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <FollowUpsPage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout>
            <SettingsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/upload" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <UploadLeadsPage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <Layout>
            <AdminPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/appointments" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <AppointmentsPage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      <Route path="/workflows" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <WorkflowsPage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      <Route path="/integrations" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <IntegrationsPage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      <Route path="/forms" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <FormsPage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      <Route path="/forms/analytics" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <FormsPage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      <Route path="/forms/:formId" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <FormsPage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      <Route path="/forms/:formId/share" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <FormsPage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      <Route path="/funnels" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <FunnelsPage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      <Route path="/inbox" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <InboxPage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      <Route path="/pipeline-config" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <PipelineConfigPage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      <Route path="/custom-fields" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <CustomFieldsPage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      <Route path="/manual-queue" element={
        <ProtectedRoute>
          <Layout>
            <SmartManualQueue />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/funnels/:funnelId" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <FunnelsPage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      <Route path="/funnels/:funnelId/analytics" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <FunnelsPage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      <Route path="/help" element={
        <ProtectedRoute>
          <GeneralOnly>
            <Layout>
              <HelpPage />
            </Layout>
          </GeneralOnly>
        </ProtectedRoute>
      } />
      {/* Training centre routes */}
      <Route path="/registrations" element={
        <ProtectedRoute>
          <Layout>
            <RegistrationsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/courses" element={
        <ProtectedRoute>
          <Layout>
            <CoursesPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/reviews" element={
        <ProtectedRoute>
          <Layout>
            <ReviewsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/student-pipeline" element={
        <ProtectedRoute>
          <Layout>
            <StudentPipelinePage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/referrals" element={
        <ProtectedRoute>
          <Layout>
            <ReferralsPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/support" element={
        <ProtectedRoute>
          <Layout>
            <SupportPage />
          </Layout>
        </ProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
